import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import CatalogProductsAPI from '../../api/catalogProducts';
import { throwErrorMessage } from '../utils/api';

export const state = {
  records: [],
  currentProduct: null,
  braavoCategories: [],
  braavoSyncStatus: { status: 'idle' },
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isImporting: false,
    isSyncing: false,
    isFetchingCategories: false,
  },
};

export const getters = {
  getProducts($state) {
    return $state.records;
  },
  getProduct: $state => id => {
    return $state.records.find(p => p.id === Number(id)) || null;
  },
  getCurrentProduct($state) {
    return $state.currentProduct;
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getBraavoCategories($state) {
    return $state.braavoCategories;
  },
  getBraavoSyncStatus($state) {
    return $state.braavoSyncStatus;
  },
};

export const actions = {
  fetchProducts: async ({ commit }) => {
    commit(types.SET_CATALOG_PRODUCT_UI_FLAG, { isFetching: true });
    try {
      const response = await CatalogProductsAPI.get();
      commit(types.SET_CATALOG_PRODUCTS, response.data);
    } catch (error) {
      // ignore
    } finally {
      commit(types.SET_CATALOG_PRODUCT_UI_FLAG, { isFetching: false });
    }
  },

  fetchProduct: async ({ commit }, id) => {
    commit(types.SET_CATALOG_PRODUCT_UI_FLAG, { isFetchingItem: true });
    try {
      const { data } = await CatalogProductsAPI.show(id);
      commit(types.SET_CATALOG_PRODUCT_CURRENT, data);
      commit(types.ADD_CATALOG_PRODUCT, data);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_CATALOG_PRODUCT_UI_FLAG, { isFetchingItem: false });
    }
  },

  createProduct: async ({ commit }, productData) => {
    commit(types.SET_CATALOG_PRODUCT_UI_FLAG, { isCreating: true });
    try {
      const response = await CatalogProductsAPI.create(productData);
      commit(types.ADD_CATALOG_PRODUCT, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_CATALOG_PRODUCT_UI_FLAG, { isCreating: false });
    }
    return null;
  },

  updateProduct: async ({ commit }, { id, data }) => {
    commit(types.SET_CATALOG_PRODUCT_UI_FLAG, { isUpdating: true });
    try {
      const response = await CatalogProductsAPI.update(id, data);
      commit(types.EDIT_CATALOG_PRODUCT, response.data);
      commit(types.SET_CATALOG_PRODUCT_CURRENT, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_CATALOG_PRODUCT_UI_FLAG, { isUpdating: false });
    }
    return null;
  },

  deleteProduct: async ({ commit }, id) => {
    commit(types.SET_CATALOG_PRODUCT_UI_FLAG, { isDeleting: true });
    try {
      await CatalogProductsAPI.delete(id);
      commit(types.DELETE_CATALOG_PRODUCT, id);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_CATALOG_PRODUCT_UI_FLAG, { isDeleting: false });
    }
  },

  importCsv: async ({ commit, dispatch }, file) => {
    commit(types.SET_CATALOG_PRODUCT_UI_FLAG, { isImporting: true });
    try {
      const { data } = await CatalogProductsAPI.importCsv(file);
      await dispatch('fetchProducts');
      return data;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.SET_CATALOG_PRODUCT_UI_FLAG, { isImporting: false });
    }
  },

  syncBraavo: async ({ commit }, payload = {}) => {
    commit(types.SET_CATALOG_PRODUCT_UI_FLAG, { isSyncing: true });
    try {
      const { data } = await CatalogProductsAPI.syncBraavo(payload);
      return data;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.SET_CATALOG_PRODUCT_UI_FLAG, { isSyncing: false });
    }
  },

  fetchBraavoCategories: async ({ commit }) => {
    commit(types.SET_CATALOG_PRODUCT_UI_FLAG, { isFetchingCategories: true });
    try {
      const { data } = await CatalogProductsAPI.braavoCategories();
      commit(types.SET_BRAAVO_CATEGORIES, data);
      return data;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.SET_CATALOG_PRODUCT_UI_FLAG, {
        isFetchingCategories: false,
      });
    }
  },

  fetchBraavoSyncStatus: async ({ commit }) => {
    try {
      const { data } = await CatalogProductsAPI.braavoSyncStatus();
      commit(types.SET_BRAAVO_SYNC_STATUS, data);
      return data;
    } catch (error) {
      return throwErrorMessage(error);
    }
  },

  clearCurrent: ({ commit }) => {
    commit(types.SET_CATALOG_PRODUCT_CURRENT, null);
  },
};

export const mutations = {
  [types.SET_CATALOG_PRODUCT_UI_FLAG]($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },
  [types.SET_CATALOG_PRODUCTS]: MutationHelpers.set,
  [types.ADD_CATALOG_PRODUCT]: MutationHelpers.setSingleRecord,
  [types.EDIT_CATALOG_PRODUCT]: MutationHelpers.update,
  [types.DELETE_CATALOG_PRODUCT]: MutationHelpers.destroy,
  [types.SET_CATALOG_PRODUCT_CURRENT]($state, product) {
    $state.currentProduct = product;
  },
  [types.SET_BRAAVO_CATEGORIES]($state, categories) {
    $state.braavoCategories = categories || [];
  },
  [types.SET_BRAAVO_SYNC_STATUS]($state, status) {
    $state.braavoSyncStatus = status || { status: 'idle' };
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
